// Per-thread "messages disappear after N seconds" timer.
//
// Ported from DisappearingMessageDuration.swift. Modelled on Signal's
// expiration timer: a single non-negative integer of seconds encoded inside
// the encrypted message body so the server never sees the TTL.
//
// `.off` is canonically `0` on the wire — never `undefined`, never a missing
// field — so the schema always has the field and old/new clients can't disagree
// about what "no timer" means.

export type DisappearingMessageDurationValue =
  | { kind: "off" }
  | { kind: "seconds"; seconds: number };

// Signal's preset ladder. Kept identical to iOS so cross-platform syncing of
// timer changes never has to round to an unfamiliar value.
export const DISAPPEARING_MESSAGE_PRESETS_SECONDS: number[] = [
  0,
  30,
  60 * 5,
  60 * 30,
  60 * 60,
  60 * 60 * 8,
  60 * 60 * 24,
  60 * 60 * 24 * 7,
  60 * 60 * 24 * 7 * 4,
];

export class DisappearingMessageDuration {
  private readonly _seconds: number;

  private constructor(seconds: number) {
    this._seconds = seconds > 0 ? seconds : 0;
  }

  static readonly off = new DisappearingMessageDuration(0);

  /** Construct from a raw seconds integer. Values ≤ 0 are normalised to .off. */
  static fromSeconds(s: number): DisappearingMessageDuration {
    return s > 0 ? new DisappearingMessageDuration(s) : DisappearingMessageDuration.off;
  }

  get seconds(): number {
    return this._seconds;
  }

  get isOff(): boolean {
    return this._seconds === 0;
  }

  /** Milliseconds, for use with Date arithmetic. */
  get ms(): number {
    return this._seconds * 1000;
  }

  // Signal's preset ladder as DisappearingMessageDuration instances.
  static readonly presets: DisappearingMessageDuration[] =
    DISAPPEARING_MESSAGE_PRESETS_SECONDS.map(DisappearingMessageDuration.fromSeconds);

  get displayName(): string {
    const s = this._seconds;
    if (s === 0)            return "Off";
    if (s < 60)             return `${s}s`;
    if (s < 60 * 60)        return `${Math.floor(s / 60)}m`;
    if (s < 60 * 60 * 24)   return `${Math.floor(s / 3600)}h`;
    if (s < 60 * 60 * 24 * 7) return `${Math.floor(s / 86_400)}d`;
    return `${Math.floor(s / (86_400 * 7))}w`;
  }

  // -------------------------------------------------------------------------
  // Codable: a single non-negative Int (mirrors iOS JSONEncoder)

  toJSON(): number {
    return this._seconds;
  }

  static fromJSON(raw: unknown): DisappearingMessageDuration {
    if (typeof raw !== "number" || !Number.isInteger(raw)) {
      return DisappearingMessageDuration.off;
    }
    return DisappearingMessageDuration.fromSeconds(raw);
  }

  // -------------------------------------------------------------------------
  // Equality

  equals(other: DisappearingMessageDuration): boolean {
    return this._seconds === other._seconds;
  }
}
